import React from 'react';

const THREAD_STATUS_LABEL = {
  valid: 'Valid',
  archived: 'Archived',
  removed: 'Removed',
  deleted: 'Deleted',
  filtered: 'Filtered',
  locked: 'Locked',
  unavailable: 'Unavailable',
  error: 'Error',
};

function formatChecked(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

/**
 * Compact target-thread health indicator for a RedditComment row.
 *
 * When a replacement is required: shows only the "Replacement required" pill.
 * The thread status, reason, and last-checked time are surfaced on hover via
 * the title tooltip so the row stays uncluttered.
 *
 * When no replacement is required but the thread is non-commentable: shows
 * only the thread-status badge with the reason in the tooltip.
 */
export default function ThreadHealth({ comment }) {
  if (!comment) return null;

  const status = comment.thread_status || null;
  const replacementRequired = !!comment.replacement_required;
  const reason = comment.thread_status_reason || comment.replacement_reason || '';
  const checkedLabel = formatChecked(comment.thread_last_checked_at);

  // Archived comments are inherently replacement-required (never went live),
  // so the "Replacement Req." pill is redundant there — skip it to keep the
  // row clean. The Archived status badge already conveys the meaning.
  if (comment.status === 'archived') return null;

  // Nothing to surface for healthy / not-yet-scanned targets.
  if ((!status || status === 'valid') && !replacementRequired) return null;

  // Build a rich tooltip with all the detail.
  const tooltipParts = [];
  if (status && status !== 'valid') {
    tooltipParts.push(`Thread: ${THREAD_STATUS_LABEL[status] || status}`);
  }
  if (reason) tooltipParts.push(reason);
  if (checkedLabel) tooltipParts.push(`Checked ${checkedLabel}`);
  const tooltip = tooltipParts.join(' · ') || undefined;

  // Replacement required: one pill, everything else on hover.
  if (replacementRequired) {
    return (
      <div className="rh-thread-health" title={tooltip}>
        <span className="rh-badge rh-badge-replacement">Replacement Req.</span>
      </div>
    );
  }

  // No replacement required, but thread is non-commentable: show status badge only.
  return (
    <div className="rh-thread-health" title={tooltip}>
      <span className={`rh-badge rh-badge-thread-${status}`}>
        {THREAD_STATUS_LABEL[status] || status}
      </span>
    </div>
  );
}
